import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button, Card, CardHeader } from '@/components/common';
import { getErrorMessage } from '@/services/api';
import { brokerService } from '@/services';
import {
  brokerKycService,
  type KycBankAccount,
  type KycDocument,
  type KycFinancial,
  type KycIndemnity,
  type KycKeyPerson,
} from '@/services/brokerKyc';
import {
  LICENCE_CATEGORIES,
  canEditKyc,
  canSubmit,
  kycStatusLabel,
  validateBankAccount,
  validateFinancialYear,
  validatePan,
} from './brokerKycForm';
import { useAuthStore } from '@/store';

/**
 * Broker settings -> Compliance / KYC (#331 phase 1). Broker admin only.
 * PAN, date of birth and account numbers are typed in once and shown back
 * masked; the server never returns them in full to this screen.
 */

const inputClass =
  'block w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white';

type Fields = { name: string; label: string; type?: string; required?: boolean; options?: [string, string][] }[];

function AddRow({
  fields,
  onSubmit,
  validate,
  busy,
}: {
  fields: Fields;
  onSubmit: (values: Record<string, string>) => Promise<unknown>;
  validate?: (values: Record<string, string>) => Record<string, string>;
  busy: boolean;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  return (
    <form
      className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2"
      onSubmit={async (event) => {
        event.preventDefault();
        const found = validate ? validate(values) : {};
        setErrors(found);
        if (Object.keys(found).length !== 0) return;
        setFormError(null);
        try {
          await onSubmit(values);
          setValues({}); // cleared only on success
        } catch (error) {
          setFormError(getErrorMessage(error)); // what was typed stays
        }
      }}
    >
      {fields.map((field) => (
        <label key={field.name} className="text-sm text-gray-700 dark:text-gray-300">
          {field.label}
          {field.options ? (
            <select
              className={inputClass}
              required={field.required}
              value={values[field.name] ?? ''}
              onChange={(e) => setValues({ ...values, [field.name]: e.target.value })}
            >
              <option value="">Select</option>
              {field.options.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          ) : (
            <input
              className={inputClass}
              type={field.type ?? 'text'}
              required={field.required}
              autoComplete="off"
              value={values[field.name] ?? ''}
              onChange={(e) => setValues({ ...values, [field.name]: e.target.value })}
            />
          )}
          {errors[field.name] && (
            <span role="alert" className="text-xs text-red-600">
              {errors[field.name]}
            </span>
          )}
        </label>
      ))}
      <div className="sm:col-span-2">
        {formError && (
          <p role="alert" className="mb-2 text-sm text-red-600">
            {formError}
          </p>
        )}
        <Button type="submit" disabled={busy}>
          Add
        </Button>
      </div>
    </form>
  );
}

function RowList<T extends { id: string }>({
  rows,
  render,
  onRemove,
  editable,
  isError,
  onRetry,
}: {
  rows: T[];
  render: (row: T) => string;
  onRemove: (row: T) => void;
  editable: boolean;
  isError?: boolean;
  onRetry?: () => void;
}) {
  if (isError) {
    return (
      <div role="alert" className="flex items-center gap-3 text-sm text-red-600">
        <span>Could not load this list.</span>
        <button type="button" className="underline" onClick={onRetry}>
          Retry
        </button>
      </div>
    );
  }
  if (rows.length === 0) return <p className="text-sm text-gray-500">None added yet.</p>;
  return (
    <ul className="divide-y divide-gray-200 text-sm dark:divide-gray-700">
      {rows.map((row) => (
        <li key={row.id} className="flex items-center justify-between py-2">
          <span className="text-gray-800 dark:text-gray-200">{render(row)}</span>
          {editable && (
            <button type="button" className="text-xs text-red-600" onClick={() => onRemove(row)}>
              Remove
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

export function BrokerKycSection() {
  const queryClient = useQueryClient();
  const role = useAuthStore((s) => s.user?.role);
  const editable = canEditKyc(role);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['broker-kyc'] });

  const summary = useQuery({ queryKey: ['broker-kyc', 'summary'], queryFn: brokerKycService.summary });
  const broker = useQuery({ queryKey: ['broker-kyc', 'broker'], queryFn: () => brokerService.getMyBroker() });
  const persons = useQuery({ queryKey: ['broker-kyc', 'key-persons'], queryFn: () => brokerKycService.list<KycKeyPerson>('key-persons') });
  const financial = useQuery({ queryKey: ['broker-kyc', 'financial'], queryFn: () => brokerKycService.list<KycFinancial>('financial') });
  const indemnity = useQuery({ queryKey: ['broker-kyc', 'indemnity'], queryFn: () => brokerKycService.list<KycIndemnity>('indemnity') });
  const banks = useQuery({ queryKey: ['broker-kyc', 'bank-accounts'], queryFn: () => brokerKycService.list<KycBankAccount>('bank-accounts') });
  const documents = useQuery({ queryKey: ['broker-kyc', 'documents'], queryFn: () => brokerKycService.list<KycDocument>('documents') });

  const create = useMutation({
    mutationFn: ({ collection, body }: { collection: Parameters<typeof brokerKycService.create>[0]; body: Record<string, unknown> | FormData }) =>
      brokerKycService.create(collection, body),
    onSuccess: () => {
      toast.success('Saved');
      refresh();
    },
    // No toast: the form shows the server's message beside what was typed.
  });
  const remove = useMutation({
    mutationFn: ({ collection, id }: { collection: Parameters<typeof brokerKycService.remove>[0]; id: string }) =>
      brokerKycService.remove(collection, id),
    onSuccess: refresh,
    onError: (error) => toast.error(getErrorMessage(error)),
  });
  const submit = useMutation({
    mutationFn: brokerKycService.submit,
    onSuccess: () => {
      toast.success('Submitted for review');
      refresh();
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
  const categories = useMutation({
    mutationFn: (codes: string[]) => brokerService.updateBroker(broker.data!.id, { licence_categories: codes } as never),
    onSuccess: refresh,
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  if (summary.isLoading) return <p className="text-sm text-gray-500">Loading compliance record...</p>;
  if (summary.isError || !summary.data) {
    return (
      <p role="alert" className="text-sm text-red-600">
        Could not load the compliance record. Try again.
      </p>
    );
  }
  const data = summary.data;
  const status = kycStatusLabel(data.kyc_status);
  const chosen: string[] = ((broker.data as unknown as { licence_categories?: string[] })?.licence_categories) ?? [];
  const busy = create.isPending;
  const add = (collection: Parameters<typeof brokerKycService.create>[0]) => (body: Record<string, unknown>) =>
    create.mutateAsync({ collection, body });

  return (
    <div className="space-y-6">
      <Card className="space-y-3 p-6">
        <CardHeader title="Compliance / KYC" subtitle="The facts a regulator or insurer asks for. A country admin reviews them." />
        <p className="text-sm">
          Status: <strong>{status.label}</strong>
        </p>
        {data.kyc_status === 'changes_requested' && data.last_decision_remarks && (
          <p role="alert" className="text-sm text-amber-700">
            Reviewer: {data.last_decision_remarks}
          </p>
        )}
        <div aria-label="Completeness" className="h-2 w-full rounded bg-gray-200 dark:bg-gray-700">
          <div className="h-2 rounded bg-blue-600" style={{ width: `${data.completeness.percent}%` }} />
        </div>
        <p className="text-xs text-gray-500">
          {data.completeness.done} of {data.completeness.total} requirements complete
        </p>
        {data.missing.length > 0 && (
          <ul className="list-disc pl-5 text-sm text-gray-700 dark:text-gray-300">
            {data.missing.map((gap) => (
              <li key={gap.code}>{gap.message}</li>
            ))}
          </ul>
        )}
        {data.expiries.some((e) => e.state === 'expired' || e.state === 'expiring') && (
          <ul className="text-sm text-amber-700">
            {data.expiries
              .filter((e) => e.state === 'expired' || e.state === 'expiring')
              .map((e) => (
                <li key={`${e.kind}-${e.label}`}>
                  {e.label}: {e.state === 'expired' ? 'expired' : 'expiring'} on {e.valid_until}
                </li>
              ))}
          </ul>
        )}
        {editable && (
          <Button
            onClick={() => submit.mutate()}
            disabled={!canSubmit(data.kyc_status, data.missing.length) || submit.isPending}
          >
            Submit for review
          </Button>
        )}
      </Card>

      <Card className="space-y-2 p-6">
        <CardHeader title="Licence categories" subtitle="Licence number, dates, PAN, GST and CIN are edited on Broker Details." />
        <div className="flex flex-wrap gap-4">
          {LICENCE_CATEGORIES.map((category) => (
            <label key={category.code} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                disabled={!editable || !broker.data}
                checked={chosen.includes(category.code)}
                onChange={(e) =>
                  categories.mutate(e.target.checked ? [...chosen, category.code] : chosen.filter((c) => c !== category.code))
                }
              />
              {category.label}
            </label>
          ))}
        </div>
      </Card>

      <Card className="p-6">
        <CardHeader title="Principal Officer and key persons" />
        <RowList
          rows={persons.data ?? []}
          isError={persons.isError}
          onRetry={() => persons.refetch()}
          editable={editable}
          render={(p) => `${p.role_display}: ${p.full_name}${p.pan_masked ? ` (PAN ${p.pan_masked})` : ''}`}
          onRemove={(p) => remove.mutate({ collection: 'key-persons', id: p.id })}
        />
        {editable && (
          <AddRow
            busy={busy}
            fields={[
              { name: 'role', label: 'Role', required: true, options: [['principal_officer', 'Principal Officer'], ['kmp', 'Key Managerial Person'], ['director', 'Director'], ['compliance_officer', 'Compliance Officer']] },
              { name: 'full_name', label: 'Full name', required: true },
              { name: 'pan_number', label: 'PAN' },
              { name: 'date_of_birth', label: 'Date of birth', type: 'date' },
              { name: 'email', label: 'Email', type: 'email' },
              { name: 'phone', label: 'Phone' },
            ]}
            validate={(v): Record<string, string> => {
              const message = v.pan_number ? validatePan(v.pan_number) : null;
              return message ? { pan_number: message } : {};
            }}
            onSubmit={(v) =>
              add('key-persons')({ ...v, pan_number: v.pan_number?.toUpperCase() ?? '', date_of_birth: v.date_of_birth || null })
            }
          />
        )}
      </Card>

      <Card className="p-6">
        <CardHeader title="Financial standing" subtitle="One row per financial year." />
        <RowList
          rows={financial.data ?? []}
          isError={financial.isError}
          onRetry={() => financial.refetch()}
          editable={editable}
          render={(f) => `${f.fy}: net worth ${f.net_worth ?? '-'}, deposit ${f.security_deposit_amount ?? '-'}`}
          onRemove={(f) => remove.mutate({ collection: 'financial', id: f.id })}
        />
        {editable && (
          <AddRow
            busy={busy}
            fields={[
              { name: 'fy', label: 'Financial year (2025-26)', required: true },
              { name: 'net_worth', label: 'Net worth', type: 'number' },
              { name: 'security_deposit_amount', label: 'Security deposit', type: 'number' },
            ]}
            validate={(v): Record<string, string> => {
              const message = validateFinancialYear(v.fy ?? '');
              return message ? { fy: message } : {};
            }}
            onSubmit={(v) =>
              add('financial')({ fy: v.fy, net_worth: v.net_worth || null, security_deposit_amount: v.security_deposit_amount || null })
            }
          />
        )}
      </Card>

      <Card className="p-6">
        <CardHeader title="Professional indemnity" />
        <RowList
          rows={indemnity.data ?? []}
          isError={indemnity.isError}
          onRetry={() => indemnity.refetch()}
          editable={editable}
          render={(p) => `${p.insurer_name} ${p.policy_number}: ${p.valid_from} to ${p.valid_until}${p.is_current ? ' (current)' : ''}`}
          onRemove={(p) => remove.mutate({ collection: 'indemnity', id: p.id })}
        />
        {editable && (
          <AddRow
            busy={busy}
            fields={[
              { name: 'insurer_name', label: 'Insurer', required: true },
              { name: 'policy_number', label: 'Policy number', required: true },
              { name: 'sum_insured', label: 'Sum insured', type: 'number', required: true },
              { name: 'valid_from', label: 'Valid from', type: 'date', required: true },
              { name: 'valid_until', label: 'Valid until', type: 'date', required: true },
            ]}
            onSubmit={(v) => add('indemnity')({ ...v, is_current: true })}
          />
        )}
      </Card>

      <Card className="p-6">
        <CardHeader title="Bank accounts" subtitle="Account numbers are stored encrypted and shown masked." />
        <RowList
          rows={banks.data ?? []}
          isError={banks.isError}
          onRetry={() => banks.refetch()}
          editable={editable}
          render={(b) => `${b.bank_name} ${b.account_masked} (${b.ifsc})${b.is_primary ? ' - primary' : ''}`}
          onRemove={(b) => remove.mutate({ collection: 'bank-accounts', id: b.id })}
        />
        {editable && (
          <AddRow
            busy={busy}
            fields={[
              { name: 'bank_name', label: 'Bank', required: true },
              { name: 'ifsc', label: 'IFSC', required: true },
              { name: 'account_holder', label: 'Account holder', required: true },
              { name: 'account_number', label: 'Account number', required: true },
              { name: 'purpose', label: 'Purpose', options: [['commission', 'Commission'], ['premium_collection', 'Premium collection'], ['settlement', 'Settlement'], ['other', 'Other']] },
              { name: 'is_primary', label: 'Primary account', options: [['true', 'Yes'], ['false', 'No']] },
            ]}
            validate={(v) => validateBankAccount({ ifsc: v.ifsc ?? '', account_number: v.account_number ?? '' })}
            onSubmit={(v) =>
              add('bank-accounts')({
                ...v,
                ifsc: v.ifsc.toUpperCase(),
                purpose: v.purpose || 'commission',
                is_primary: v.is_primary === 'true',
              })
            }
          />
        )}
      </Card>

      <Card className="p-6">
        <CardHeader title="Documents" subtitle="PAN, GST, CIN and the IRDAI licence are required." />
        <RowList
          rows={documents.data ?? []}
          isError={documents.isError}
          onRetry={() => documents.refetch()}
          editable={editable}
          render={(d) => `${d.doc_type_display}: ${d.status_display}${d.remarks ? ` - ${d.remarks}` : ''}`}
          onRemove={(d) => remove.mutate({ collection: 'documents', id: d.id })}
        />
        {editable && (
          <form
            className="mt-3 flex flex-wrap items-end gap-3"
            onSubmit={async (event) => {
              event.preventDefault();
              const formEl = event.currentTarget;
              const form = new FormData(formEl);
              if (!(form.get('file') as File)?.size) return;
              setUploadError(null);
              try {
                await create.mutateAsync({ collection: 'documents', body: form });
                formEl.reset(); // cleared only on success
              } catch (error) {
                setUploadError(getErrorMessage(error)); // the chosen type and file stay
              }
            }}
          >
            <label className="text-sm">
              Type
              <select name="doc_type" required className={inputClass} defaultValue="">
                <option value="" disabled>
                  Select
                </option>
                <option value="pan">PAN</option>
                <option value="gst">GST certificate</option>
                <option value="cin">Certificate of incorporation</option>
                <option value="irdai_licence">IRDAI licence</option>
                <option value="pi_certificate">Professional indemnity certificate</option>
                <option value="net_worth_certificate">Net worth certificate</option>
                <option value="deposit_proof">Security deposit proof</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label className="text-sm">
              File
              <input name="file" type="file" required accept=".pdf,.jpg,.jpeg,.png" className={inputClass} />
            </label>
            <Button type="submit" disabled={busy}>
              Upload
            </Button>
            {uploadError && (
              <p role="alert" className="w-full text-sm text-red-600">
                {uploadError}
              </p>
            )}
          </form>
        )}
      </Card>
    </div>
  );
}
